import { logError, logInfo } from '../utilities/logging';
import { CartItem } from './addToCart';

export const createImageSwitchPayload = ({
  item
}: {
  item: CartItem;
}): { newPayload: { id: string; properties: Record<string, string> } | false } => {
  const { properties } = item;

  // Skip items that don't have properties
  if (!properties || typeof properties !== 'object') {
    console.log('Skipping item without properties:', item);
    return { newPayload: false };
  }

  // Parse _personalisation_base_attributes first to prepare for IMAGE_UPLOAD URLs
  let baseAttributes = properties['_personalisation_base_attributes'];
  if (typeof baseAttributes === 'string') {
    try {
      baseAttributes = JSON.parse(baseAttributes);
    } catch (error) {
      logError({
        label: 'addImageUrls',
        error,
        message: 'Failed to parse _personalisation_base_attributes JSON string',
        extraData: { baseAttributesString: baseAttributes }
      });
      baseAttributes = {};
    }
  }
  if (!baseAttributes || typeof baseAttributes !== 'object') {
    baseAttributes = {};
  }

  const newProps = Object.entries({ ...properties }).reduce((acc, [key, value]) => {
    if (typeof value === 'string' && value?.includes('https://cdn.shopify.com/s/files/')) {
      // Move all the uploaded images to base attributes.
      baseAttributes[key] = value;
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});

  // @TODO: Remove this when we have a better flow for handling visualiser images.
  const baseAttributesEntries = Object.entries(baseAttributes);
  const baseAttributesNoDupes = baseAttributesEntries.reduce((acc, [key, value]) => {
    // Check if there are any matching values for this in baseAttributes with a key that ends up 'Visual' that isn't itself.
    if (baseAttributesEntries.some(([key2, value2]) => key2.endsWith(' Visual') && value2 === value && key2 !== key)) {
      return acc;
    }
    acc[key.replace(' - Visual', ' Visual')] = value;
    return acc;
  }, {});

  // Always add the updated _personalisation_base_attributes
  newProps['_personalisation_base_attributes'] = baseAttributesNoDupes;

  // For cart changes, we need to use the cart item key
  // The key is a unique identifier for the cart item that includes the variant ID and any properties
  if (!item.key) {
    logError({
      label: 'addImageUrls',
      error: new Error('Cart item key not found - cannot update cart item'),
      message: 'Cart item key not found - cannot update cart item',
      extraData: { item },
      throwError: true
    });
  }

  // Only proceed if there are actual changes to make
  const hasPropertyChanges = Object.keys(newProps).length > 0;

  if (!hasPropertyChanges) {
    logInfo({
      label: 'addImageUrls',
      message: 'No changes needed for item',
      extraData: { item }
    });
    return { newPayload: false };
  }

  const newPayload = {
    id: item.key,
    properties: newProps
  };

  // Validate the payload before sending
  if (!newPayload.id) {
    logError({
      label: 'addImageUrls',
      error: new Error(`Invalid cart item ID: ${newPayload.id}`),
      message: `Invalid cart item ID: ${newPayload.id}`,
      extraData: { newPayload },
      throwError: true
    });
  }

  if (!newPayload.properties || typeof newPayload.properties !== 'object') {
    logError({
      label: 'addImageUrls',
      error: new Error('Invalid properties object'),
      message: 'Invalid properties object',
      extraData: { newPayload },
      throwError: true
    });
  }

  return { newPayload };
};
